import { Autocomplete, Box, Grid, styled } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import { resetModal } from 'app/slice/modal.slice'
import { ButtonInfoSimple } from 'component/Button/ButtonInfo'
import InputCheckbox from 'component/Input/InputCheckbox'
import Loading from 'component/Loading'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import SearchPatients from 'component/SearchPatients'
/** CONSTANTS */
import { colors } from 'constants/theme'

/** API */
import LabOrderApi, { LabOrderDetailInterface, LabOrderInterface, PayloadLabOrderDetails, PlayloadLabOrder } from 'api/lab-order.api'
import ClinicApi from 'api/master/clinic.api'
import LabCompanyApi from 'api/setting/default-labs/lab-company.api'
import LabGroupsApi from 'api/setting/default-labs/lab-group.api'
import DateInput from 'new-components/inputs/date-input'
import SelectInput from 'new-components/inputs/select-input'
import TextAreaInput from 'new-components/inputs/text-area-input'
import TextInput from 'new-components/inputs/text-input'
import TimeInput from 'new-components/inputs/time-input'

const initStateErrorMessage = {
  PATIENT: '',
  VN_NUMBER: '',

  VN_NUMBER_STRING_EMPTY: '',

  DATE_ORDER_STRING_EMPTY: '',
  TIME_ORDER_STRING_EMPTY: '',
  USER_ID_NUMBER_BASE: '',
  USER_ID_ANY_REQUIRED: '',
  LAB_COMPANY_ID_NUMBER_BASE: '',
  LAB_COMPANY_ID_ANY_REQUIRED: '',
  DATE_EXPORT_STRING_EMPTY: '',
  DATE_IMPORT_STRING_EMPTY: '',
  OPERATIVE: '',
  LAB_GROUP_ID_NUMBER_BASE: '',
  LAB_GROUP_ID_ANY_REQUIRED: ''
}

const AutoCompleteForm = styled(Autocomplete)(({ theme }) => ({
  '& input.MuiOutlinedInput-input': { height: '30px' }, '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: '#F0F0F0',
  },
}))

/** INTERFACE */
export interface FormLabOrderProps {
  showForm: boolean
  labOrderId?: number
  patientId?: number
  patientCaseId?: number
  patient?: any
  vnNumber?: string
  handleSuccess?: () => void
  onReset?: () => void
  onDelete?: (id: number, obj?: any) => void
  onSuccess?: () => void
}
export default function FormLabOrder(props: FormLabOrderProps) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()

  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState({ ...initStateErrorMessage })

  /** DATA LIST */
  const [visitOptions, setVisitOptions] = useState<any[]>([])
  const [doctorOptions, setDoctorOptions] = useState<any[]>([])
  const [labCompanyOptions, setLabCompanyOptions] = useState<any[]>([])
  const [operativeOptions, setOperativeOptions] = useState<any[]>([])
  const [labGroupOptions, setLabGroupOptions] = useState<any[]>([])


  // --- เพิ่ม STATE สำหรับ LAZY LOADING ตรงนี้ ---
  const [labGroupPage, setLabGroupPage] = useState(0);
  const [hasMoreLabGroups, setHasMoreLabGroups] = useState(true);

  const [labCompanyPage, setLabCompanyPage] = useState(0);
  const [hasMoreLabCompanies, setHasMoreLabCompanies] = useState(true);

  const [doctorPage, setDoctorPage] = useState(0);
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true);

  /** FORM */
  const [patientId, setPatientId] = useState<number>(props.patientId || 0)
  const [patientCaseId, setPatientCaseId] = useState<number>(props?.patientCaseId || 0)
  const [patient, setPatient] = useState(props.patient || {})
  const [vnNumber, setVnNumber] = useState<string>(props.vnNumber || '')
  const [labOrderNumber, setLabOrderNumber] = useState<string>('-')
  const [labOrderStatus, setLabOrderStatus] = useState<string>('-')
  const disabledForm: boolean = patientId === 0 && vnNumber === ''
  const [dateOrder, setDateOrder] = useState<string>('')
  const [timeOrder, setTimeOrder] = useState<string>('')
  const [dateExport, setDateExport] = useState<string>('')
  const [dateImport, setDateImport] = useState<string>('')
  const [userId, setUserId] = useState<number>(0)
  const [labCompanyId, setLabCompanyId] = useState<number>(0)
  const [serviceId, setServiceId] = useState<number>(0)
  const [service, setService] = useState<any>(null)
  const [labGroupId, setLabGroupId] = useState<number>(0)
  const [labGroup, setLabGroup] = useState<any>(null)
  const [labOrderDetails, setLabOrderDetails] = useState<PayloadLabOrderDetails[]>([])
  const [remark, setRemark] = useState<string>('')

  /** LOADING */
  const [loadingOrderLab, setLoadingOrderLab] = useState<boolean>(false)
  const [loadingDataLab, setLoadingDataLab] = useState<boolean>(false)
  const [loadingCompany, setLoadingCompany] = useState<boolean>(false)
  const [loadingFormLab, setLoadingFormLab] = useState<boolean>(false)
  const [loadingDoctor, setLoadingDoctor] = useState<boolean>(false)
  const [isInitialLabLoad, setIsInitialLabLoad] = useState(true);

  const [submitBtn, setSubmitBtn] = useState<boolean>(false)

  /** FUNCTION */
  useEffect(() => {
    loadOptions()
    onSelectPatient(props?.patientId || 0)
  }, [props.showForm])

  useEffect(() => {
    getTreatmentInVisit()
  }, [props.showForm])

  useEffect(() => {
    if (props.patientCaseId) setPatientCaseId(props.patientCaseId)
  }, [props.patientCaseId])

  const loadLabOrder = async (id: number) => {
    await LabOrderApi.findById(id).then(async ({ data }) => {
      await renderEdit(data).finally(() => {
        setLoadingDataLab(false)
        setIsEdit(true)
      })
    })
  }


  const loadLabGroups = useCallback(async (nextPage = false) => {
    if (loadingFormLab || (nextPage && !hasMoreLabGroups)) return;
    setLoadingFormLab(false);
    const pageToFetch = nextPage ? labGroupPage + 1 : 1;
    try {
      const res = await LabGroupsApi.findAll({ page: pageToFetch, pageLimit: 50, status: 'ACTIVE' });
      const data = res.data || [];
      if (data.length < 50) { setHasMoreLabGroups(false); }
      setLabGroupOptions(prev => (pageToFetch === 1 ? data : [...prev, ...data]));
      setLabGroupPage(pageToFetch);
      if (isInitialLabLoad) { setIsInitialLabLoad(false); }
    } catch (error) {
      console.error("Failed to load lab groups", error);
    } finally {
      setLoadingFormLab(false);
    }
  }, [loadingFormLab, hasMoreLabGroups, labGroupPage, isInitialLabLoad]);

  const loadOptions = async () => {
    setLoadingCompany(true)
    setLoadingFormLab(true)
    setLoadingDoctor(true)

    loadLabGroups()
    const condition: any = { page: 1, pageLimit: 50, status: 'ACTIVE' }
    await LabCompanyApi.findAll(condition)
      .then(({ data }) => {
        setLabCompanyOptions(data)
      })
      .finally(() => setLoadingCompany(false))
    await ClinicApi.findAllDoctors()
      .then(({ data }) => {
        setDoctorOptions(data)
      })
      .finally(() => setLoadingDoctor(false))
    if (props?.labOrderId && props.showForm) {
      setLoadingOrderLab(true)
      await loadLabOrder(props?.labOrderId || 0).finally(() => setLoadingOrderLab(false))
    }
  }

  useEffect(() => {
    clearErrorMessage()
  }, [patientId, patientCaseId, vnNumber, labOrderNumber, labOrderStatus, dateOrder, timeOrder, dateExport, dateImport, userId, labCompanyId, serviceId, labGroupId, labOrderDetails])

  const renderEdit = async (objEdit: LabOrderInterface) => {
    if (props?.labOrderId && objEdit) {
      setLoadingDataLab(true)
      await ClinicApi.findPatientsById(objEdit.patientId)
        .then(({ data }) => setPatient(data))
        .catch((e) => {
          return
        })
      await onSelectPatient(objEdit.patientId)
      await onSelectVnNumber(objEdit.vnNumber)
      setPatientCaseId(objEdit.patientCaseId)
      setLabOrderNumber(objEdit.labOrderNumber)
      setLabOrderStatus(objEdit.labOrderStatus)
      setDateOrder(objEdit.dateOrder)
      setTimeOrder(`${objEdit.timeOrder.split(':')[0]}:${objEdit.timeOrder.split(':')[1]}`)
      setDateExport(objEdit.dateExport)
      setDateImport(objEdit.dateImport)
      setUserId(objEdit.userId)
      setLabCompanyId(objEdit.labCompanyId)
      setServiceId(objEdit.tempOrderTreatmentId)
      const operatives: any[] = await LabOrderApi.findTreatmentVisit(objEdit.patientCaseId).then(({ data }) => data)
      setOperativeOptions(operatives)
      setService(operatives.find((op: any) => op.labOrderId === objEdit.labOrderId) || null)
      setLabGroupId(objEdit.labGroupId)
      setRemark(objEdit.remark)
      if (objEdit?.labOrderDetails) {
        const tempDetails: PayloadLabOrderDetails[] | any[] =
          objEdit?.labOrderDetails?.map((detail: LabOrderDetailInterface) => {
            return {
              labId: detail.labId,
              labTypeId: detail.labTypeId,
              inputSupport: detail.inputSupport,
              labText: detail?.labText || ''
            }
          }) || []
        setLabOrderDetails(tempDetails)
      }
    }
  }

  useEffect(() => {
    setLabGroup(_.find(labGroupOptions, (op: any) => op.labGroupId === labGroupId) || null)
  }, [labGroupId, labGroupOptions])

  const getTreatmentInVisit = (pcId?: number) => {
    const id = pcId !== undefined && pcId !== 0 ? pcId : props?.patientCaseId ? props?.patientCaseId : 0
    LabOrderApi.findTreatmentVisit(id).then(({ data }) => {
      setOperativeOptions(data)
    })
  }

  const handleCheckLab = (e: any, objLab: any) => {
    const { labId, labTypeId, inputSupport } = objLab
    const checked: boolean = e?.target?.checked || false
    const tempDetail: PayloadLabOrderDetails = { labId, labTypeId, inputSupport, labText: '' }
    if (checked) setLabOrderDetails([...labOrderDetails, tempDetail])
    else setLabOrderDetails(labOrderDetails.filter((lab: any) => !(lab.labId === labId && lab.labTypeId === labTypeId)))
  }

  const handleCheckLabAddText = (e: any, objLab: any) => {
    const { labId, labTypeId } = objLab
    const val: boolean = e?.target?.value || ''
    const tempDetail: PayloadLabOrderDetails | undefined = [...labOrderDetails].find((lab: any) => lab.labId === labId && lab.labTypeId === labTypeId)
    if (tempDetail) {
      const temp: PayloadLabOrderDetails[] = [...labOrderDetails.filter((lab: any) => !(lab.labId === labId && lab.labTypeId === labTypeId))]
      setLabOrderDetails([...temp, { ...tempDetail, labText: String(val) }])
    }
  }

  const submit = () => {
    if (!patientId || !vnNumber || !dateOrder || !timeOrder || !userId || !labCompanyId || !dateExport || !dateImport || !service || !labGroupId) {
      const msg: any = {
        PATIENT: !patientId ? t(`LAB_ORDER.MESSAGE.PATIENT`) : '',
        VN_NUMBER: !vnNumber ? t(`LAB_ORDER.MESSAGE.VN_NUMBER`) : '',
        DATE_ORDER_STRING_EMPTY: !dateOrder ? t(`LAB_ORDER.MESSAGE.DATE_ORDER_STRING_EMPTY`) : '',
        TIME_ORDER_STRING_EMPTY: !timeOrder ? t(`LAB_ORDER.MESSAGE.TIME_ORDER_STRING_EMPTY`) : '',
        USER_ID_NUMBER_BASE: !userId ? t(`LAB_ORDER.MESSAGE.USER_ID_NUMBER_BASE`) : '',
        LAB_COMPANY_ID_NUMBER_BASE: !labCompanyId ? t(`LAB_ORDER.MESSAGE.LAB_COMPANY_ID_NUMBER_BASE`) : '',
        DATE_EXPORT_STRING_EMPTY: !dateExport ? t(`LAB_ORDER.MESSAGE.DATE_EXPORT_STRING_EMPTY`) : '',
        DATE_IMPORT_STRING_EMPTY: !dateImport ? t(`LAB_ORDER.MESSAGE.DATE_IMPORT_STRING_EMPTY`) : '',
        OPERATIVE: !service ? t(`LAB_ORDER.MESSAGE.OPERATIVE`) : '',
        LAB_GROUP_ID_NUMBER_BASE: !labGroupId ? t(`LAB_ORDER.MESSAGE.LAB_GROUP_ID_NUMBER_BASE`) : ''
      }
      setErrorMessage({ ...initStateErrorMessage, ...msg })
      return false
    }
    if (labOrderDetails.length === 0) {
      notiError(t(`LAB_ORDER.MESSAGE.ERROR.ERROR_SELECT_LAB`))
      return false
    }
    const opSelect: any = _.find(operativeOptions, (op: any) => op.tempOrderTreatmentId === serviceId || op.orderTreatmentId === serviceId)
    if (!opSelect) return false
    const id = patientCaseId || props?.patientCaseId || 0
    const payload: PlayloadLabOrder = {
      patientCaseId: id,
      dateOrder,
      timeOrder,
      userId,
      labCompanyId,
      dateExport,
      dateImport,
      tempOrderTreatmentId: opSelect?.tempOrderTreatmentId || null,
      orderTreatmentId: opSelect?.orderTreatmentId || null,
      labGroupId,
      remark,
      labOrderDetails
    }

    if (props?.labOrderId) update(props.labOrderId, payload)
    else create(payload)
  }

  const create = async (payload: PlayloadLabOrder) => {
    setSubmitBtn(true)
    await LabOrderApi.create(payload)
      .then((res: any) => {
        setSubmitBtn(false)
        if (res.status !== undefined && res.status === 201) {
          if (props?.handleSuccess) props?.handleSuccess()
          dispatch(resetModal())
          resetForm();
          if (props.onReset) props.onReset()
          if (props.onSuccess) props.onSuccess()
          notiSuccess(t(`LAB_ORDER.MESSAGE.SUCCESS.CREATE`), '', null, null, 'register-page')
        } else {
          const err = res.response.data
          setErrorMessage({ ...initStateErrorMessage, [err.message]: t(`LAB_ORDER.MESSAGE.${err.message}`) })
        }
      })
      .catch((e) => {
        setSubmitBtn(false)
        const err = e.response.data
        notiError(t(`LAB_ORDER.MESSAGE.ERROR.${err.message}`))
      })
  }

  const update = async (labOrderId: number, payload: PlayloadLabOrder) => {
    await LabOrderApi.update(labOrderId, payload)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          if (props?.handleSuccess) props?.handleSuccess()
          dispatch(resetModal())
          resetForm();
          if (props.onReset) props.onReset()
          if (props.onSuccess) props.onSuccess()
          notiSuccess(t(`LAB_ORDER.MESSAGE.SUCCESS.UPDATE`), '', null, null, 'register-page')
        } else {
          const err = res.response.data
          setErrorMessage({ ...initStateErrorMessage, [err.message]: t(`LAB_ORDER.MESSAGE.${err.message}`) })
        }
      })
      .catch((e) => {
        const err = e.response.data
        notiError(t(`LAB_ORDER.MESSAGE.ERROR.${err.message}`))
      })
  }

  const resetForm = async () => {
    clearErrorMessage()
    setPatientId(0)
    setPatientCaseId(0)
    setPatient([])
    setVnNumber('')
    setLabOrderNumber('-')
    setLabOrderStatus('-')
    setDateOrder('')
    setTimeOrder('')
    setDateExport('')
    setDateImport('')
    setUserId(0)
    setLabGroupId(0)
    setLabGroup(null)
    setLabCompanyId(0)
    setServiceId(0)
    setService(null)
    setLabOrderDetails([])
    setRemark('')
    setIsEdit(false)
    setOperativeOptions([])
    setVisitOptions([])
    // if (props.onReset) props.onReset()
  }

  const clearErrorMessage = async () => setErrorMessage(initStateErrorMessage)

  const onSelectPatient = async (valuePatientId: number) => {
    setPatientId(valuePatientId)
    let visits: any[] = []
    if (valuePatientId) {
      await LabOrderApi.findVisitHasOperative(valuePatientId).then(({ data }) => {
        setVisitOptions(data)
        visits = data
      })
    } else {
      setVisitOptions([])
      visits = []
    }
    if (!props.patientCaseId) await onSelectVnNumber('')
    else await onSelectVnNumber(visits.find((visit: any) => visit.patientCaseId === props.patientCaseId)?.vnNumber || '')
    setServiceId(0)
    setService(null)
  }

  const onSelectVnNumber = async (val: string) => {
    setVnNumber(val)
    if (val) {
      const pcId: any = visitOptions.find((visit: any) => visit.vnNumber === val)?.patientCaseId || 0
      getTreatmentInVisit(pcId)
      setPatientCaseId(pcId)
      setServiceId(0)
      setService(null)
    } else {
      setPatientCaseId(0)
    }
  }

  const getLabelLabOrderStatus = (status: string) => {
    let label: any = ''
    if (status === 'ORDER_LAB') label = t('LAB_ORDER.LAB_STATUS.ORDER_LAB')
    else if (status === 'SEND_LAB') label = t('LAB_ORDER.LAB_STATUS.SEND_LAB')
    else if (status === 'RECEIVE_LAB') label = t('LAB_ORDER.LAB_STATUS.RECEIVE_LAB')
    else if (status === 'RECEIVE_BILL') label = t('LAB_ORDER.LAB_STATUS.RECEIVE_BILL')
    else if (status === 'REPLY_LAB') label = t('LAB_ORDER.LAB_STATUS.REPLY_LAB')
    else if (status === 'CANCEL_LAB') label = t('LAB_ORDER.LAB_STATUS.CANCEL_LAB')
    else if (status === 'CLOSE_LAB') label = t('LAB_ORDER.LAB_STATUS.CLOSE_LAB')
    return label
  }


  return (
    (props.showForm && ((props.labOrderId && isEdit) || true) && (
      <ModalCustom
        size={'xl'}
        title={props.labOrderId ? t('LAB_ORDER.UPDATE') : t('LAB_ORDER.CREATE')}
        alignFooter={'end'}
        fullscreen={'lg-down'}
        closeButton
        component={
          <>
            {(!loadingOrderLab && !loadingFormLab && !loadingCompany && !loadingDoctor && !loadingDataLab && (
              <>
                {(!props.patientId || !props.patientCaseId) && (
                  <>
                    <Grid container sx={{ mx: 0, pt: { xs: '8px', lg: 0 } }}>
                      <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                        {t('LAB_ORDER.FORM.NO_LAB')}: {labOrderNumber}
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                        {t('LAB_ORDER.FORM.STATUS')}: {getLabelLabOrderStatus(labOrderStatus)}
                      </Grid>
                    </Grid>
                    <Grid container sx={{ mx: 0, pt: { xs: '8px', lg: 0 } }}>
                      <Grid item xs={12} sm={6} lg={6} sx={{ mb: '16px', px: '8px' }}>
                        <SearchPatients
                          status="ACTIVE"
                          bodyHeight={42}
                          callbackPatientId={onSelectPatient}
                          patient={props.patient || patient}
                          patientId={patientId}
                          disabled={props.labOrderId !== 0 ? true : false}
                          onClear={() => {
                            onSelectPatient(0)
                            resetForm()
                          }}
                          helperText={errorMessage.PATIENT || ''}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                        <SelectInput
                          required={true}
                          label={t('VN')}
                          value={vnNumber}
                          options={_.map(visitOptions, (visit: any, i: number) => ({ value: visit.vnNumber, name: visit.vnNumber })).reverse()}
                          helperText={errorMessage.VN_NUMBER || ''}
                          onChange={(event: any) => onSelectVnNumber(event?.target.value || '')}
                          disabled={disabledForm}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid container sx={{ mx: 0, pt: { xs: '8px', lg: 0 } }}>
                  <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                    <DateInput
                      required
                      fullWidth
                      disabled={disabledForm}
                      key={'date-order'}
                      label={t('LAB_ORDER.FORM.DATE_ORDER')}
                      language={i18n.language.toUpperCase() as any}
                      value={dateOrder}
                      helperText={errorMessage.DATE_ORDER_STRING_EMPTY || ''}
                      onChange={(e: any) => setDateOrder(moment(e).format('YYYY-MM-DD'))}
                      onClear={() => setDateOrder('')}
                      marginBottomErrorText='10px'
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                    <TimeInput
                      label={t('LAB_ORDER.FORM.TIME_ORDER')}
                      value={timeOrder}
                      startHour='00:00'
                      endHour='23:59'
                      required
                      disabled={disabledForm}
                      helperText={errorMessage.TIME_ORDER_STRING_EMPTY || ''}
                      onChange={(e) => setTimeOrder(e)}
                      onClear={() => setTimeOrder('')}
                      marginBottomErrorText='10px'
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
                      <SelectInput
                        required
                        label={t('LAB_ORDER.FORM.DOCTOR')}
                        width={240}
                        value={userId === 0 ? '' : userId}
                        options={_.map(doctorOptions, doctor => ({
                          value: doctor.userId,
                          name: doctor.fullname
                        }))}
                        helperText={
                          errorMessage.USER_ID_NUMBER_BASE ||
                          errorMessage.USER_ID_ANY_REQUIRED ||
                          ''
                        }
                        onChange={e => setUserId(e.target.value)}
                        disabled={disabledForm}
                      />
                      <Box sx={{
                        marginTop: (errorMessage.USER_ID_NUMBER_BASE ||
                          errorMessage.USER_ID_ANY_REQUIRED ||
                          '') ? -2 : 0
                      }}>
                        <ButtonInfoSimple
                          text={t('LAB_ORDER.FORM.DOCTOR_ORDER_LAB')}
                        /></Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                    <SelectInput
                      required={true}
                      label={t('LAB_ORDER.FORM.COMPANY')}
                      value={labCompanyId === 0 ? '' : labCompanyId}
                      options={_.map(labCompanyOptions, (labCompany: any, i: number) => ({ value: labCompany.labCompanyId, name: labCompany.labCompanyName }))}
                      helperText={errorMessage.LAB_COMPANY_ID_NUMBER_BASE || ''}
                      onChange={(val: any) => {
                        setLabCompanyId(val.target.value)
                      }}
                      disabled={disabledForm}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                    <DateInput
                      required
                      fullWidth
                      disabled={disabledForm}
                      key={'date-export'}
                      label={t('LAB_ORDER.FORM.DATE_EXPORT')}
                      value={dateExport}
                      helperText={errorMessage.DATE_EXPORT_STRING_EMPTY || ''}
                      onChange={(e: any) => setDateExport(moment(e).format('YYYY-MM-DD'))}
                      marginBottomErrorText='16px'
                      onClear={() => setDateExport('')}
                      language={i18n.language.toUpperCase() as any}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3} sx={{ mb: '16px', px: '8px' }}>
                    <DateInput
                      required
                      fullWidth
                      disabled={disabledForm}
                      key={'date-receive'}
                      label={t('LAB_ORDER.FORM.DATE_RECEIVE')}
                      value={dateImport}
                      helperText={errorMessage.DATE_IMPORT_STRING_EMPTY || ''}
                      onChange={(e: any) => setDateImport(moment(e).format('YYYY-MM-DD'))}
                      marginBottomErrorText='16px'
                      onClear={() => setDateImport('')}
                      language={i18n.language.toUpperCase() as any}
                    />
                  </Grid>
                </Grid>
                <Grid container sx={{ mx: 0, pt: { xs: '8px', lg: 0 } }}>
                  <Grid item xs={12} sm={12} lg={6} sx={{ mb: '16px', px: '8px' }}>
                    <SelectInput
                      required
                      label={t('LAB_ORDER.FORM.OPERATIVE')}
                      value={service ? (service.tempOrderTreatmentId || service.orderTreatmentId) : ''}
                      options={operativeOptions.map(op => ({
                        value: op.tempOrderTreatmentId || op.orderTreatmentId,
                        name: op.operativeName,
                        disabled: op.labOrderId !== null && op.labOrderId !== props.labOrderId
                      }))}
                      helperText={errorMessage.OPERATIVE || ''}
                      onChange={e => {
                        const val = e.target.value
                        const sel = operativeOptions.find(
                          o => (o.tempOrderTreatmentId || o.orderTreatmentId) === val
                        ) || null
                        setService(sel)
                        if (sel) {
                          setServiceId(sel.tempOrderTreatmentId || sel.orderTreatmentId)
                        }
                      }}
                      disabled={disabledForm || props.vnNumber === '' || vnNumber === ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} lg={6} sx={{ mb: '16px', px: '8px' }}>
                    {/* <SelectInput
                      required
                      label={t('LAB_ORDER.FORM.LAB_GROUP')}
                      value={labGroupId === 0 ? '' : labGroupId}
                      options={labGroupOptions.map(lg => ({
                        value: lg.labGroupId,
                        name: lg.labGroupName
                      }))}
                      helperText={errorMessage.LAB_GROUP_ID_NUMBER_BASE || ''}
                      onChange={e => {
                        const val = e.target.value as number
                        setLabGroupId(val)
                        setLabOrderDetails([])
                      }}
                      disabled={disabledForm}
                    /> */}
                    <SelectInput
                      required
                      label={t('LAB_ORDER.FORM.LAB_GROUP')}
                      value={labGroupId === 0 ? '' : labGroupId}
                      options={labGroupOptions.map(lg => ({
                        value: lg.labGroupId,
                        name: lg.labGroupName
                      }))}
                      helperText={errorMessage.LAB_GROUP_ID_NUMBER_BASE || ''}
                      onChange={e => {
                        const val = e.target.value as number
                        setLabGroupId(val)
                        setLabOrderDetails([])
                      }}
                      disabled={disabledForm}
                      loading={loadingFormLab}
                      onOpen={() => { if (isInitialLabLoad) loadLabGroups(false) }}
                      onScrollEnd={() => loadLabGroups(true)}
                    />
                  </Grid>
                </Grid>
                <Grid container className="custom-scroll" sx={{ mx: 0, overflow: 'auto' }} style={{ maxHeight: 500, minHeight: labGroup ? 0 : 200 }}>
                  {labGroup ? (
                    <>
                      {_.map(_.groupBy(labGroup?.labGroupDetails, 'labTypeId'), (detail: any) => (
                        <Grid container sx={{ mx: 0 }}>
                          {_.map(detail, (labDetail: any, i: number) => {
                            const { labId, labTypeId, inputSupport } = labDetail
                            const findDetail: PayloadLabOrderDetails | undefined = _.find([...labOrderDetails], (lab: any) => lab.labId === labId && lab.labTypeId === labTypeId)
                            const isChecked: boolean = findDetail ? true : false
                            const isLabInput: boolean = inputSupport === 'SUPPORT' ? true : false

                            return (
                              <>
                                {i === 0 && (
                                  <Grid item xs={12}>
                                    <Box className="p-2 mb-2" sx={{ backgroundColor: colors.themeSecondColor10, fontSize: 18, fontWeight: 500 }}>
                                      {labDetail.labTypeName}
                                    </Box>
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                  <Grid container className="flex-center" sx={{ mx: 0, mb: '8px' }}>
                                    <Grid item xs={12} sm={6} className="text-text-nowrap-ellipsis" sx={{ px: '4px', mr: 'auto' }}>
                                      <InputCheckbox checked={isChecked} label={labDetail.labName} onChange={(e) => handleCheckLab(e, labDetail)} />
                                    </Grid>
                                    {isLabInput && (
                                      <Grid item xs={12} sm={6} sx={{ px: '4px' }}>
                                        <TextInput fullWidth label={t('LAB_ORDER.FORM.REMARK')} value={isChecked ? findDetail?.labText : ''} disabled={!isChecked} onChange={(e) => handleCheckLabAddText(e, labDetail)} />
                                      </Grid>
                                    )}
                                  </Grid>
                                </Grid>
                              </>
                            )
                          })}
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <div className="d-flex w-100">
                      <div className="flex-center text-center w-100">{t('LAB_ORDER.FORM.NONE_DATA')}</div>
                    </div>
                  )}
                </Grid>
                <div className="px-2">
                  <hr />
                </div>
                <div className="mt-2 px-2">
                  <TextAreaInput fullWidth disabled={disabledForm} value={remark} onChange={(event) => setRemark(event.target.value)} label={t('LAB_ORDER.FORM.REMARK')} />
                </div>
              </>
            )) || (
                <div className="mx-auto w-max">
                  <Loading show type={'softLoading'} />
                </div>
              )}
          </>
        }
        onSubmit={submit}
        disabledSubmit={submitBtn}
        onReset={() => {
          resetForm();
          if (props.onReset) props.onReset()
        }}
        textBtnCancel={t('LAB_ORDER.FORM.CANCEL')}
        textBtnConfirm={props.labOrderId ? t('LAB_ORDER.FORM.SAVE') : t('LAB_ORDER.FORM.ADD')}
      />
    )) || <></>
  )
}
